"use client";

/**
 * Command Palette (Cmd/Ctrl+K)
 * 
 * Central action registry for navigation, creation, and actions.
 * Provides a unified way to access all app functionality.
 */

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Search, Sparkles, Plus, Settings, Home, MessageSquare, FolderKanban, FileText, Zap } from "lucide-react";
import { useUser } from "@/lib/user/context";
import { getUserAIs } from "@/lib/ai/entityManager";
import type { AIEntity } from "@/types/aiEntity";

interface Command {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  action: () => void;
  category: "navigation" | "create" | "ai" | "action";
  keywords?: string[];
}

export default function CommandPalette() {
  const router = useRouter();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [commands, setCommands] = useState<Command[]>([]);
  const [ais, setAIs] = useState<AIEntity[]>([]);

  // Load user AIs
  useEffect(() => {
    if (user) {
      getUserAIs(user.id).then(setAIs);
    }
  }, [user]);

  // Build command list
  useEffect(() => {
    const cmds: Command[] = [
      // Navigation
      {
        id: "home",
        label: "Go to Home",
        description: "View all your AIs",
        icon: <Home className="w-4 h-4" />,
        action: () => router.push("/home"),
        category: "navigation",
        keywords: ["home", "dashboard", "ais"],
      },
      {
        id: "chat",
        label: "Open Chat",
        description: "Start chatting with your AI",
        icon: <MessageSquare className="w-4 h-4" />,
        action: () => router.push("/chat"),
        category: "navigation",
        keywords: ["chat", "talk", "conversation"],
      },
      {
        id: "projects",
        label: "Projects",
        description: "View your projects",
        icon: <FolderKanban className="w-4 h-4" />,
        action: () => router.push("/projects"),
        category: "navigation",
        keywords: ["projects", "workspace"],
      },
      {
        id: "settings",
        label: "Settings",
        description: "Configure your AI and preferences",
        icon: <Settings className="w-4 h-4" />,
        action: () => router.push("/settings"),
        category: "navigation",
        keywords: ["settings", "config", "preferences"],
      },
      // Create
      {
        id: "new-ai",
        label: "Create New AI",
        description: "Build a new AI assistant",
        icon: <Plus className="w-4 h-4" />,
        action: () => router.push("/start"),
        category: "create",
        keywords: ["new", "create", "build", "make"],
      },
      {
        id: "new-project",
        label: "New Project",
        description: "Create a new project",
        icon: <FolderKanban className="w-4 h-4" />,
        action: () => router.push("/projects/new"),
        category: "create",
        keywords: ["new", "project", "workspace"],
      },
      // AI switching
      ...ais.map(ai => ({
        id: `switch-${ai.id}`,
        label: `Switch to ${ai.name}`,
        description: ai.description || "Switch to this AI",
        icon: <Sparkles className="w-4 h-4" />,
        action: () => router.push(`/chat?aiId=${ai.id}`),
        category: "ai" as const,
        keywords: [ai.name.toLowerCase(), "switch", "ai"],
      })),
    ];

    setCommands(cmds);
  }, [router, ais]);

  // Filter commands by query
  const filteredCommands = commands.filter(cmd => {
    if (!query) return true;
    const lowerQuery = query.toLowerCase();
    return (
      cmd.label.toLowerCase().includes(lowerQuery) ||
      cmd.description?.toLowerCase().includes(lowerQuery) ||
      cmd.keywords?.some(k => k.includes(lowerQuery))
    );
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        return;
      }

      // Handle navigation when open
      if (open) {
        if (e.key === "Escape") {
          setOpen(false);
          setQuery("");
          setSelectedIndex(0);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredCommands.length - 1 ? prev + 1 : prev
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            setOpen(false);
            setQuery("");
            setSelectedIndex(0);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredCommands, selectedIndex]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] px-4"
      onClick={() => {
        setOpen(false);
        setQuery("");
        setSelectedIndex(0);
      }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Palette */}
      <div
        className="relative w-full max-w-2xl premium-white-glass rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.6)",
        }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 p-4 border-b border-black/10">
          <Search className="w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent outline-none text-lg"
            autoFocus
          />
          <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded">
            ESC
          </kbd>
        </div>

        {/* Commands list */}
        <div className="max-h-96 overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No commands found
            </div>
          ) : (
            <div className="p-2">
              {filteredCommands.map((cmd, index) => (
                <button
                  key={cmd.id}
                  onClick={() => {
                    cmd.action();
                    setOpen(false);
                    setQuery("");
                    setSelectedIndex(0);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    index === selectedIndex
                      ? "bg-black/10"
                      : "hover:bg-black/5"
                  }`}
                >
                  <div className="text-gray-600">{cmd.icon}</div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">{cmd.label}</div>
                    {cmd.description && (
                      <div className="text-sm text-gray-500">{cmd.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-black/10 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Enter</kbd>
              Select
            </span>
          </div>
          <span>{filteredCommands.length} command{filteredCommands.length !== 1 ? "s" : ""}</span>
        </div>
      </div>
    </div>
  );
}

